"use client";

import { useCallback, useEffect, useId, useLayoutEffect, useRef, useState } from "react";
import { Knopf } from "../knopf";

/**
 * Feldhilfe (Musterkatalog M-060, Design-Guide `feldhilfe.md`; Betreiber 13.10.2026 „W94 Empfehlung“, FQ1 bis FQ6): ein „?“ am Label
 * (oder hinter einem Begriff im Fließtext, `Kurzhilfe`, M-012 KF6) öffnet per Klick/Tipp eine kleine Karte mit „Wozu?“ (1 bis 2 Sätze)
 * und „Beispiel“, optional dem Link „Mehr dazu“ und bei leeren Mehrzeilern dem Knopf „Beispiel übernehmen“.
 *
 * Abgrenzung: `hilfe` am `Feld` bleibt der kurze, immer sichtbare Pflichthinweis (steht in `aria-describedby` des Feldes). Die
 * Feldhilfe ist der optionale Hintergrund; sie steht bewusst NICHT in `aria-describedby`, damit Hilfstechnik sie nicht bei
 * jedem Fokus vorliest. Fehler und Ablehnungen gehören nie in die Feldhilfe (F3/F4). Kein Hover-Auslöser (Touch), kein Tooltip.
 *
 * Mechanik wie die Personen-Karte (Escape schließt, Fokus kehrt zum Auslöser zurück, Klick daneben, Scrollen und Größenänderung
 * schließen), aber die Karte bleibt im DOM des Feldes (kein Portal): im Modal bleibt sie so Teil von Fokusfalle und Escape-Vorrang.
 * Sie liegt `position: fixed` und wird zur Laufzeit am Auslöser platziert (wie `schwebende-liste.ts`); unter 640 px füllt sie die
 * Breite. Der Baustein kennt keine Rechte und keine Quellen: der Aufrufer reicht nur Text aus dem Hilfe-Katalog des Moduls.
 * KEINE Messung, wie oft eine Hilfe geöffnet wird (r177).
 */
export interface FeldhilfeInhalt {
  /** „Wozu?“: 1 bis 2 Sätze, höchstens 280 Zeichen (Prüfung im Katalog-Helfer `definiereFeldhilfen`). */
  wozu: string;
  /** „Beispiel“: ein Satz oder Wert, höchstens 200 Zeichen. */
  beispiel?: string;
  /** Knopf „Beispiel übernehmen“ anbieten: wirkt nur in einem Mehrzeiler und nur, solange er leer ist (FQ5). */
  beispielUebernehmbar?: boolean;
  /** Interner Pfad auf Funktionsbeschreibung oder Guided Tour (R-C), z. B. `/hilfe/entscheidungen`. */
  mehrHref?: string;
  /** Beschriftung des Links; Standard „Mehr dazu“. */
  mehrText?: string;
}

/** Zweite Sperre neben dem Katalog-Helfer: nur interne Pfade (kein `//`, kein `/\`, kein Backslash); sonst wird kein Link gerendert. */
const INTERNER_PFAD = /^\/(?![/\\])[^\s\\]*$/;

const ABSTAND = 8;
const SCHMAL = 640;
const SEITENRAND = 16;

// Feste, vollständige Ketten (nie zusammengesetzt, R8). Trefferfläche 44 px (Touch), im Layout nur 24 px breit/hoch.
const AUSLOESER =
  "inline-flex justify-center items-center shrink-0 size-11 -my-2.5 -me-2.5 rounded-full text-muted-foreground-1 hover:text-foreground focus:outline-hidden focus-visible:ring-2 focus-visible:ring-primary-focus cursor-pointer";
const AUSLOESER_OFFEN =
  "inline-flex justify-center items-center shrink-0 size-11 -my-2.5 -me-2.5 rounded-full text-foreground focus:outline-hidden focus-visible:ring-2 focus-visible:ring-primary-focus cursor-pointer";
const KARTE = "block fixed top-0 start-0 z-70 w-72 sm:w-80 whitespace-normal bg-popover border border-popover-line rounded-xl shadow-md text-start focus:outline-hidden";
const ABSCHNITT_KOPF = "flex items-center gap-x-1.5 text-xs font-medium uppercase text-muted-foreground-1";

const SVG = {
  className: "shrink-0 size-4",
  xmlns: "http://www.w3.org/2000/svg",
  width: 24,
  height: 24,
  viewBox: "0 0 24 24",
  fill: "none",
  stroke: "currentColor",
  strokeWidth: 2,
  strokeLinecap: "round" as const,
  strokeLinejoin: "round" as const,
  "aria-hidden": true as const,
  focusable: "false" as const,
};

/** Lucide „circle-help“ (R-F). */
function SymbolHilfe() {
  return (
    <>
      {/* Preline: ui-components/ui-components-sub-menus-selection/ui-components-cat-profile-popovers/contact-profile-popover-card (Symbol Lucide „circle-help“) */}
      <svg {...SVG}>
        <circle cx="12" cy="12" r="10" />
        <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" />
        <path d="M12 17h.01" />
      </svg>
    </>
  );
}

/** Lucide „lightbulb“. */
function SymbolBeispiel() {
  return (
    <svg {...SVG}>
      <path d="M15 14c.2-1 .7-1.7 1.5-2.5 1-.9 1.5-2.2 1.5-3.5A6 6 0 0 0 6 8c0 1 .2 2.2 1.5 3.5.7.7 1.3 1.5 1.5 2.5" />
      <path d="M9 18h6" />
      <path d="M10 22h4" />
    </svg>
  );
}

/** Lucide „book-open“. */
function SymbolBuch() {
  return (
    <svg {...SVG}>
      <path d="M12 7v14" />
      <path d="M3 18a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1h5a4 4 0 0 1 4 4 4 4 0 0 1 4-4h5a1 1 0 0 1 1 1v13a1 1 0 0 1-1 1h-6a3 3 0 0 0-3 3 3 3 0 0 0-3-3z" />
    </svg>
  );
}

/** Lucide „x“. */
function SymbolSchliessen() {
  return (
    <svg {...SVG}>
      <path d="M18 6 6 18" />
      <path d="m6 6 12 12" />
    </svg>
  );
}

/** Setzt den Wert eines Textfeldes so, dass React-`onChange` (kontrolliert) und natives `input` (unkontrolliert) feuern. */
function schreibeWert(ziel: HTMLTextAreaElement, text: string): void {
  const setter = Object.getOwnPropertyDescriptor(HTMLTextAreaElement.prototype, "value")?.set;
  if (setter) setter.call(ziel, text);
  else ziel.value = text;
  ziel.dispatchEvent(new Event("input", { bubbles: true }));
}

/** Das Mehrzeilen-Feld mit dieser Id, wenn es existiert (nur dort gibt es „Beispiel übernehmen“). */
function findeMehrzeiler(id: string | undefined): HTMLTextAreaElement | null {
  if (!id || typeof document === "undefined") return null;
  const el = document.getElementById(id);
  return el instanceof HTMLTextAreaElement ? el : null;
}

interface Eigenschaften {
  /** Name des Feldes bzw. des Begriffs: Titel der Karte und Teil des zugänglichen Namens des Auslösers. */
  titel: string;
  inhalt: FeldhilfeInhalt;
  /** Id des Mehrzeilers, in den „Beispiel übernehmen“ schreibt; fehlt sie, gibt es den Knopf nicht. */
  uebernehmenZiel?: string;
}

/**
 * Auslöser plus Karte. Wird vom `Feld` neben dem Label gesetzt (`feldhilfe`) und als `Kurzhilfe` im Fließtext benutzt.
 * Der Auslöser ist ein `<button>`: Enter und Leertaste öffnen, Escape schließt und gibt den Fokus zurück.
 */
function FeldhilfeKern({ titel, inhalt, uebernehmenZiel }: Eigenschaften) {
  const [offen, setOffen] = useState(false);
  // Beim Öffnen festgehalten: ist das Ziel ein leerer Mehrzeiler? (Nicht bei jedem Render, damit der Knopf beim Tippen nicht verschwindet.)
  const [kannUebernehmen, setKannUebernehmen] = useState(false);
  const ausloeser = useRef<HTMLButtonElement | null>(null);
  const karte = useRef<HTMLDivElement | null>(null);
  const id = useId().replace(/:/g, "");
  const karteId = `fh${id}`;
  const titelId = `${karteId}-titel`;
  const wozuId = `${karteId}-wozu`;

  const schliesse = useCallback((fokusZurueck: boolean) => {
    setOffen(false);
    if (fokusZurueck) ausloeser.current?.focus();
  }, []);

  const umschalten = () => {
    if (offen) {
      schliesse(false);
      return;
    }
    const ziel = findeMehrzeiler(uebernehmenZiel);
    setKannUebernehmen(Boolean(inhalt.beispielUebernehmbar && inhalt.beispiel && ziel && ziel.value === ""));
    setOffen(true);
  };

  // Platzieren am Auslöser (fixed, randbewusst, bei Platzmangel oben); schmal: volle Breite abzüglich Seitenrand.
  useLayoutEffect(() => {
    if (!offen) return;
    const k = karte.current;
    const a = ausloeser.current;
    if (!k || !a) return;
    const vw = document.documentElement.clientWidth;
    const vh = window.innerHeight;
    const schmal = vw < SCHMAL;
    k.style.width = schmal ? `${vw - 2 * SEITENRAND}px` : "";
    const auslB = a.getBoundingClientRect();
    const karteB = k.getBoundingClientRect();
    let links = schmal ? SEITENRAND : auslB.left;
    let oben = auslB.bottom + ABSTAND;
    if (oben + karteB.height > vh - ABSTAND && auslB.top - ABSTAND - karteB.height > ABSTAND) oben = auslB.top - ABSTAND - karteB.height;
    links = Math.max(ABSTAND, Math.min(links, vw - karteB.width - ABSTAND));
    k.style.left = `${Math.round(links)}px`;
    k.style.top = `${Math.round(Math.max(ABSTAND, oben))}px`;
  });

  // Fokus in die Karte (Tab erreicht dann Schließen, Beispiel übernehmen, Mehr dazu).
  useEffect(() => {
    if (offen) karte.current?.focus();
  }, [offen]);

  useEffect(() => {
    if (!offen) return;
    const beiKlick = (e: PointerEvent) => {
      const ziel = e.target as Node;
      if (karte.current?.contains(ziel) || ausloeser.current?.contains(ziel)) return;
      setOffen(false);
    };
    const beiTaste = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        // Escape schließt nur die Karte, nicht das Modal darunter.
        e.stopPropagation();
        setOffen(false);
        ausloeser.current?.focus();
      }
    };
    const beiScroll = (e: Event) => {
      if (karte.current && e.target instanceof Node && karte.current.contains(e.target)) return;
      setOffen(false);
    };
    const beiGroesse = () => setOffen(false);
    document.addEventListener("pointerdown", beiKlick);
    document.addEventListener("keydown", beiTaste, true);
    document.addEventListener("scroll", beiScroll, true);
    window.addEventListener("resize", beiGroesse);
    return () => {
      document.removeEventListener("pointerdown", beiKlick);
      document.removeEventListener("keydown", beiTaste, true);
      document.removeEventListener("scroll", beiScroll, true);
      window.removeEventListener("resize", beiGroesse);
    };
  }, [offen]);

  const uebernehmen = () => {
    const ziel = findeMehrzeiler(uebernehmenZiel);
    // Zweite Prüfung beim Klick: nur in ein noch leeres Feld schreiben, nie über Eingaben.
    if (ziel && inhalt.beispiel && ziel.value === "") {
      schreibeWert(ziel, inhalt.beispiel);
      setOffen(false);
      ziel.focus();
      ziel.select();
    }
  };

  return (
    <>
      {/* Preline: ui-components/ui-components-sub-menus-selection/ui-components-cat-profile-popovers/contact-profile-popover-card */}
      <button
        ref={ausloeser}
        type="button"
        className={offen ? AUSLOESER_OFFEN : AUSLOESER}
        aria-label={`Hilfe zu „${titel}“`}
        aria-haspopup="dialog"
        aria-expanded={offen}
        aria-controls={offen ? karteId : undefined}
        onClick={umschalten}
      >
        <SymbolHilfe />
      </button>
      {offen && (
        // Nur Phrasing-Elemente (`span` mit Block-Klassen): `Kurzhilfe` steht in einem Absatz, `p`/`div` darin wären ungültiges HTML.
        <span ref={karte} id={karteId} role="dialog" aria-labelledby={titelId} aria-describedby={wozuId} tabIndex={-1} className={KARTE}>
          <span className="block p-4 space-y-3">
            <span className="flex items-start justify-between gap-x-2">
              <span id={titelId} className="block text-sm font-semibold text-foreground break-words">
                {titel}
              </span>
              <Knopf nurSymbol variante="ghost" groesse="xs" label="Hilfe schließen" symbol={<SymbolSchliessen />} onClick={() => schliesse(true)} />
            </span>
            <span className="block space-y-1">
              <span className={ABSCHNITT_KOPF}>Wozu?</span>
              <span id={wozuId} className="block text-sm text-foreground">
                {inhalt.wozu}
              </span>
            </span>
            {inhalt.beispiel && (
              <span className="block space-y-1">
                <span className={ABSCHNITT_KOPF}>
                  <SymbolBeispiel />
                  Beispiel
                </span>
                <span className="block text-sm text-foreground italic">{`„${inhalt.beispiel}“`}</span>
                {kannUebernehmen && (
                  <span className="block pt-1">
                    <Knopf variante="sekundaer" groesse="xs" onClick={uebernehmen}>
                      Beispiel übernehmen
                    </Knopf>
                  </span>
                )}
              </span>
            )}
            {inhalt.mehrHref && INTERNER_PFAD.test(inhalt.mehrHref) && (
              <span className="block pt-3 border-t border-popover-line">
                <Knopf href={inhalt.mehrHref} variante="text" symbol={<SymbolBuch />}>
                  {inhalt.mehrText ?? "Mehr dazu"}
                </Knopf>
              </span>
            )}
          </span>
        </span>
      )}
    </>
  );
}

/**
 * Auslöser am Label eines Feldes. Nicht direkt benutzen: `Feld` setzt ihn, wenn `feldhilfe` angegeben ist
 * (Aufrufer reichen den Katalogeintrag als `feldhilfe` an das Feld, nie eigene Texte).
 */
export function Feldhilfe({ label, inhalt, mehrzeilerId }: { label: string; inhalt: FeldhilfeInhalt; mehrzeilerId?: string }) {
  return <FeldhilfeKern titel={label} inhalt={inhalt} uebernehmenZiel={mehrzeilerId} />;
}

/**
 * Variante „am Wort“ (M-012 KF6, „Kurzhilfe“): der Begriff steht im Fließtext, das „?“ direkt dahinter; dieselbe Karte,
 * kein „Beispiel übernehmen“. Der Begriff selbst bleibt normaler Text (kein zweiter Auslöser).
 */
export function Kurzhilfe({ begriff, inhalt }: { begriff: string; inhalt: FeldhilfeInhalt }) {
  return (
    <span className="inline-flex items-center gap-x-1 whitespace-nowrap">
      {begriff}
      <FeldhilfeKern titel={begriff} inhalt={{ ...inhalt, beispielUebernehmbar: false }} />
    </span>
  );
}
